import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Download, ClipboardList, AlertTriangle } from "lucide-react";

const OPTIONS = ["Strongly Disagree", "Disagree", "Neutral", "Agree", "Strongly Agree", "Not Applicable / Not Addressed"];
const BAR_COLORS = ["#B91C1C", "#EA580C", "#A8A29E", "#4A7C59", "#166534", "#D6CDB8"];

export default function Evaluations() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState(null);
  const [schools, setSchools] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [filters, setFilters] = useState({ school_id: "", consultant_id: "" });

  const load = () => {
    const q = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    api.get(`/evaluations/summary?${q}`).then((r) => setSummary(r.data));
    api.get(`/evaluations?${q}`).then((r) => setData(r.data));
  };
  useEffect(() => {
    load();
    api.get("/organizations").then((r) => setSchools(r.data.flatMap((o) => o.schools)));
    if (isAdmin) api.get("/admin/consultants").then((r) => setConsultants(r.data));
  }, [filters]);

  const exportCsv = async () => {
    const r = await api.get("/evaluations/export", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "evaluation-responses.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported.");
  };

  if (!summary || !data) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="evaluations-loading">Loading evaluations…</div>;

  return (
    <div className="max-w-6xl space-y-6" data-testid="evaluations-page">
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <p className="eyebrow">Teacher feedback</p>
          <h1 className="section-title text-3xl mt-1">Evaluation Results</h1>
          <p className="mt-3 text-sm text-[#57534E] max-w-3xl" data-testid="eval-framing-note">{summary.framing}</p>
        </div>
        {isAdmin && <button className="btn-outline" onClick={exportCsv} data-testid="export-evaluations-button"><Download size={15} /> Export CSV</button>}
      </div>

      <div className="flex flex-wrap gap-3 fade-up-1" data-testid="eval-filters">
        <select className="input max-w-[240px]" value={filters.school_id} data-testid="eval-filter-school"
          onChange={(e) => setFilters({ ...filters, school_id: e.target.value })}>
          <option value="">All schools</option>
          {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {isAdmin && (
          <select className="input max-w-[240px]" value={filters.consultant_id} data-testid="eval-filter-consultant"
            onChange={(e) => setFilters({ ...filters, consultant_id: e.target.value })}>
            <option value="">All consultants</option>
            {consultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <div className="badge-approved self-center" data-testid="eval-total-badge">{summary.total_responses} response{summary.total_responses === 1 ? "" : "s"}</div>
      </div>

      <div className="card p-6 space-y-5 fade-up-2" data-testid="eval-distributions-card">
        <h2 className="font-serif font-bold text-lg text-navy">“As a result of this session or service, I am better prepared to…”</h2>
        {summary.per_statement.map((st, i) => {
          const total = Math.max(1, st.applicable_count + st.na_count);
          return (
            <div key={i} data-testid={`eval-dist-${i + 1}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1.5">
                <span className="text-sm font-bold text-navy">{i + 1}. {st.statement}</span>
                <span className="text-xs text-[#78716C]">{st.applicable_count} applicable · {st.na_count} N/A (excluded)</span>
              </div>
              <div className="flex h-5 rounded-full overflow-hidden bg-sand">
                {OPTIONS.map((opt, oi) => {
                  const n = st.counts[opt] || 0;
                  if (!n) return null;
                  return <div key={opt} title={`${opt}: ${n}`} style={{ width: `${(n / total) * 100}%`, background: BAR_COLORS[oi] }} />;
                })}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                {OPTIONS.map((opt, oi) => st.counts[opt] ? (
                  <span key={opt} className="text-[11px] text-[#57534E] flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm inline-block" style={{ background: BAR_COLORS[oi] }} />{opt}: {st.counts[opt]}</span>
                ) : null)}
              </div>
            </div>
          );
        })}
      </div>

      {isAdmin && data.mismatch_reports.length > 0 && (
        <div className="card p-6 fade-up-2" data-testid="mismatch-reports-card">
          <h2 className="font-serif font-bold text-lg text-navy mb-3 flex items-center gap-2"><AlertTriangle size={17} className="text-[#92400E]" /> “Details do not look correct” flags</h2>
          <ul className="space-y-2">
            {data.mismatch_reports.map((m) => (
              <li key={m.id} className="text-sm rounded-lg bg-[#FFFBEB] border border-[#FCD34D] px-4 py-2.5" data-testid={`mismatch-${m.id}`}>
                <b>{m.school_name}</b> · {m.service_date} — {m.note || "(no note)"}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="card overflow-hidden fade-up-3" data-testid="eval-responses-card">
        <div className="px-6 pt-5 pb-3 flex items-center gap-2">
          <ClipboardList size={17} className="text-terracotta" />
          <h2 className="font-serif font-bold text-lg text-navy">Individual responses (no respondent identity collected)</h2>
        </div>
        <table className="w-full">
          <thead className="bg-sand/60"><tr>
            <th className="table-th">Date</th><th className="table-th">School</th><th className="table-th">Session</th>
            {isAdmin && <th className="table-th">Consultant</th>}<th className="table-th">Form v</th><th className="table-th">Comments</th></tr></thead>
          <tbody>
            {data.responses.map((r) => (
              <tr key={r.id} className="hover:bg-canvas" data-testid={`eval-response-${r.id}`}>
                <td className="table-td font-bold">{r.service_date}</td>
                <td className="table-td">{r.school_name}</td>
                <td className="table-td">{r.session_label}</td>
                {isAdmin && <td className="table-td">{r.consultant_name}</td>}
                <td className="table-td">v{r.template_version}</td>
                <td className="table-td text-xs text-[#57534E] max-w-[280px]">
                  {[r.q2, r.q3, r.q4].filter(Boolean).join(" — ") || "—"}</td>
              </tr>
            ))}
            {data.responses.length === 0 && (
              <tr><td colSpan={6} className="table-td text-center text-[#78716C] py-8" data-testid="eval-empty">No responses yet. Responses appear after teachers submit the evaluation form — a sent link alone never counts.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
